import { useCallback, useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { downloadFile } from "../api/client.ts"
import type { CaseListItem, CaseListResponse, CaseStatus } from "../api/types.ts"
import { useApiQuery, useAsyncAction, useDebounced } from "../hooks/index.ts"
import { useI18n } from "../i18n/index.tsx"
import { useSession } from "../auth/SessionProvider.tsx"
import {
  CLEAR_ALL_FILTERS,
  PERIOD_IDS,
  activeFilterChips,
  listQuery,
  periodChange,
  periodLabelKey,
  withFilterChanges,
  type FilterChanges,
  type PeriodId,
} from "../lib/case-filters.ts"
import { matchPreset } from "../lib/date-presets.ts"
import { nextSort, sortParams, type SortDir } from "../lib/table-sort.ts"
import { caseColumns } from "../components/case-columns.tsx"
import { CaseSummary } from "../components/CaseSummary.tsx"
import { ColumnPicker } from "../components/ColumnPicker.tsx"
import { SavedViews } from "../components/SavedViews.tsx"
import { loadColumns, resolveVisible, saveColumns, toggleColumn } from "../lib/column-prefs.ts"
import { userKey } from "../lib/local-store.ts"
import {
  addView,
  loadViews,
  removeView as dropView,
  saveViews,
  type SavedView,
} from "../lib/saved-views.ts"
import { useCaseDetailPanel } from "../components/CaseDetailPanel.tsx"
import {
  Button,
  Card,
  ChipGroup,
  DataTable,
  EmptyState,
  ErrorNote,
  Field,
  FilterBar,
  FilterChips,
  Input,
  PageHeader,
  Pagination,
  SkeletonTable,
  type Column,
} from "../ui/index.ts"

const STATUSES: CaseStatus[] = [
  "registered",
  "arrived",
  "documented",
  "shipped",
  "returned",
  "discarded",
  "cancelled",
]

/** 검색은 입력이 멎으면 스스로 반영한다. Enter 를 누르거나 칸을 벗어날 필요가 없다. */
const SEARCH_DEBOUNCE_MS = 300

/**
 * 처음 보이는 칸. 나머지는 "컬럼 설정"에서 켠다 —
 * 늘 보여 주면 표가 가로로 넘치는데, 어떤 사람에게는 그 칸이 주인공이다.
 */
const DEFAULT_COLUMNS = [
  "seq",
  "orderNo",
  "customerName",
  "status",
  "docStatus",
  "receivedOn",
  "trackingNo",
  "org",
  "weight",
  "disposition",
]

export function CaseListPage() {
  const { t, locale } = useI18n()
  const { user } = useSession()
  // 필터는 URL 에 둔다. 뒤로가기와 링크 공유가 그냥 되고, 대시보드 카드가 그대로 링크가 된다.
  const [params, setParams] = useSearchParams()
  const urlQuery = params.get("q") ?? ""

  const [search, setSearch] = useState(urlQuery)
  const settledSearch = useDebounced(search, SEARCH_DEBOUNCE_MS)
  const download = useAsyncAction()

  const query = useApiQuery<CaseListResponse>(`/api/cases?${listQuery(params)}`)
  const data = query.data

  const update = useCallback(
    (changes: FilterChanges) => setParams((current) => withFilterChanges(current, changes)),
    [setParams],
  )

  // 카드 링크·뒤로가기처럼 바깥에서 q 가 바뀌면 입력칸을 맞춘다.
  // (effect 가 아니라 렌더 중에 맞춘다 — 한 번 그렸다가 고치면 옛 글자가 한 프레임 보인다.)
  const [syncedQuery, setSyncedQuery] = useState(urlQuery)
  if (syncedQuery !== urlQuery) {
    setSyncedQuery(urlQuery)
    setSearch(urlQuery)
  }

  // 반대로 입력이 멎으면 URL 에 싣는다 — 주소가 바뀌면 목록은 알아서 다시 읽힌다.
  // 아직 멎지 않았으면(친 값과 가라앉은 값이 다르면) 기다린다. 이 확인이 없으면
  // 칩으로 검색어를 지운 직후, 뒤늦게 가라앉은 옛 검색어가 URL 에 다시 실린다.
  useEffect(() => {
    if (search !== settledSearch) return
    if (settledSearch !== urlQuery) update({ q: settledSearch })
  }, [search, settledSearch, urlQuery, update])

  // 이 브라우저에만 남는 설정. 계정마다 따로 둔다(창고 PC 한 대를 여러 사람이 쓴다).
  const columnsKey = userKey("returns.columns.cases", user?.id)
  const viewsKey = userKey("returns.views.cases", user?.id)
  const [storedColumns, setStoredColumns] = useState<string[] | null>(() => loadColumns(columnsKey))
  const [views, setViews] = useState<SavedView[]>(() => loadViews(viewsKey))

  // 로그인한 계정이 바뀌면(다른 사람이 이어 쓰면) 그 사람의 설정으로 갈아 끼운다.
  const [settingsOwner, setSettingsOwner] = useState(columnsKey)
  if (settingsOwner !== columnsKey) {
    setSettingsOwner(columnsKey)
    setStoredColumns(loadColumns(columnsKey))
    setViews(loadViews(viewsKey))
  }

  const detail = useCaseDetailPanel({
    // 옆판에서 상태를 바꾸면 뒤 목록의 뱃지도 같이 맞춘다.
    onChanged: () => void query.reload(),
    rows: data?.items,
  })

  const page = Number(params.get("page") ?? "1")
  const size = data?.size ?? Number(params.get("size") ?? "50")
  const sort = params.get("sort")
  const dir: SortDir = params.get("dir") === "desc" ? "desc" : "asc"
  const status = params.get("status") as CaseStatus | null
  const from = params.get("from")
  const to = params.get("to")
  const preset = matchPreset(from, to, new Date())
  const customPeriod = (from !== null || to !== null) && preset === null
  const period: PeriodId | null = preset ?? (customPeriod ? "custom" : null)

  const cols = caseColumns(t, locale)
  // 고를 수 있는 칸 전부. 순서가 곧 표의 순서다.
  const allColumns: Column<CaseListItem>[] = [
    cols.seq(),
    cols.orderNo({ primary: true }),
    cols.customerName(),
    cols.customerPhone({ priority: "lg" }),
    cols.status(),
    cols.docStatus({ priority: "md" }),
    cols.returnReason({ priority: "md" }),
    cols.receivedOn({ priority: "md" }),
    cols.arrivedOn({ priority: "md" }),
    cols.inspected({ priority: "lg" }),
    cols.trackingNo({ priority: "md" }),
    cols.org({ priority: "lg" }),
    cols.weight({ priority: "lg" }),
    cols.freight({ priority: "lg" }),
    cols.codFee({ priority: "lg" }),
    cols.disposition({ priority: "lg" }),
    cols.itemCount({ priority: "lg" }),
    cols.closedOn({ priority: "lg" }),
  ]
  const columnChoices = allColumns.map((column) => ({ key: column.key, label: column.header }))
  const visibleColumnKeys = resolveVisible(columnChoices, storedColumns, DEFAULT_COLUMNS)
  const columns = allColumns.filter((column) => visibleColumnKeys.includes(column.key))

  const chips = activeFilterChips({ card: params.get("card"), q: urlQuery, status, from, to, preset })

  return (
    <>
      <PageHeader
        title={t("cases.title")}
        meta={<CaseSummary total={data?.total ?? null} />}
        actions={
          <>
            <ColumnPicker
              columns={columnChoices}
              visible={visibleColumnKeys}
              onToggle={(key) => {
                const next = toggleColumn(visibleColumnKeys, key)
                setStoredColumns(next)
                saveColumns(columnsKey, next)
              }}
              onReset={() => {
                setStoredColumns(null)
                saveColumns(columnsKey, DEFAULT_COLUMNS)
                setStoredColumns(DEFAULT_COLUMNS)
              }}
            />
            <Button
              icon="download"
              onClick={() =>
                void download.run(() =>
                  downloadFile(`/api/cases.csv?${listQuery(params)}`, "returns.csv"),
                )
              }
            >
              {t("cases.export")}
            </Button>
            {user && user.role !== "warehouse" ? (
              <Link to="/cases/new" className="btn btn--primary btn--md">
                <span>{t("nav.caseNew")}</span>
              </Link>
            ) : null}
          </>
        }
      />

      <Card>
        <FilterBar>
          <div className="filter-bar__search">
            <Field label={t("cases.search")} htmlFor="case-search">
              <Input
                id="case-search"
                icon="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Field>
          </div>

          <Field label={t("case.status")}>
            <ChipGroup
              label={t("cases.filterStatus")}
              value={status}
              allLabel={t("cases.filterAll")}
              options={STATUSES.map((value) => ({ value, label: t(`status.${value}`) }))}
              onChange={(value) => update({ status: value })}
            />
          </Field>

          <Field label={t("cases.period")}>
            <ChipGroup
              label={t("cases.period")}
              value={period}
              allLabel={t("cases.filterAll")}
              options={PERIOD_IDS.map((value) => ({ value, label: t(periodLabelKey(value)) }))}
              onChange={(value) => {
                const changes = periodChange(value, period, new Date())
                if (changes) update(changes)
              }}
            />
          </Field>

          {customPeriod ? (
            <>
              <Field label={t("cases.from")} htmlFor="case-from">
                <Input
                  id="case-from"
                  type="date"
                  value={from ?? ""}
                  onChange={(event) => update({ from: event.target.value || null })}
                />
              </Field>
              <Field label={t("cases.to")} htmlFor="case-to">
                <Input
                  id="case-to"
                  type="date"
                  value={to ?? ""}
                  onChange={(event) => update({ to: event.target.value || null })}
                />
              </Field>
            </>
          ) : null}
        </FilterBar>

        <SavedViews
          views={views}
          search={`?${listQuery(params)}`}
          onApply={(view) => setParams(new URLSearchParams(view.query))}
          onSave={(name) => {
            const next = addView(views, name, `?${listQuery(params)}`)
            setViews(next)
            saveViews(viewsKey, next)
          }}
          onRemove={(name) => {
            const next = dropView(views, name)
            setViews(next)
            saveViews(viewsKey, next)
          }}
        />

        <FilterChips
          chips={chips.map((chip) => ({
            key: chip.key,
            label: chip.labelKey ? t(chip.labelKey) : (chip.labelText ?? ""),
            onRemove: () => update(chip.clear),
          }))}
          clearAllLabel={t("cases.clearAll")}
          removeLabel={t("common.remove")}
          onClearAll={() => update(CLEAR_ALL_FILTERS)}
        />
      </Card>

      <ErrorNote error={download.error ?? query.error} />

      <Card padded={false}>
        {!data || query.loading ? (
          <div className="table-pad">
            <SkeletonTable rows={6} />
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={data.items}
            rowKey={(row) => row.id}
            onRowClick={(row) => detail.open(row.id)}
            empty={<EmptyState title={t("cases.empty")} description={t("cases.emptyHint")} />}
            sort={{
              key: sort,
              dir,
              label: t("cases.sort"),
              onSort: (key) => update(sortParams(nextSort({ sort, dir }, key))),
            }}
            footer={
              data.items.length > 0 ? (
                <Pagination
                  page={page}
                  size={size}
                  total={data.total}
                  onPage={(next) => update({ page: String(next) })}
                  onSize={(next) => update({ size: String(next), page: null })}
                />
              ) : null
            }
          />
        )}
      </Card>

      {detail.panel}
    </>
  )
}
